import React from 'react';
import { MapProvider, useMap } from '@webV2/map/MapProvider';
import { FloorMap } from '@webV2/map/MapView/FloorMap';
import { useOnMapLoaded } from '@webV2/map/MapView/useOnMapLoaded';
import { MapInteractiveZones } from '@webV2/map/MapView/MapInteractiveZones';
import { useAuthenticated, useFloors } from '@gettactic/hooks';
import { useSvgMapInteractions } from '@gettactic/helpers/src/map/svg/useSvgMapInteractions';
import { IResource } from '@gettactic/api';
import { MapViewSvgMiniMap } from '@webV2/map/MapView/MapViewSvg/MapViewSvgMiniMap';

export interface MapViewEmbeddedProps {
  officeId: string;
  floorId: string;
  focussedResourceId?: string;
  date?: string;
  enableControls?: boolean;
}

const MapViewEmbeddedInner: React.FC<MapViewEmbeddedProps> = ({
  focussedResourceId,
  enableControls
}) => {
  const { floor, events } = useMap();
  const svgRef = React.useRef<SVGSVGElement | null | undefined>(null);
  const mapRef = React.useRef<HTMLDivElement | null>(null);
  const mapContainer = React.useRef<HTMLDivElement>(null);

  const svgMapInteractions = useSvgMapInteractions(
    svgRef,
    mapRef,
    mapContainer,
    {
      allowInteractionsWhenFocussed: true,
      tiltMax: 0,
      offsetTop: -50
    }
  );

  const {
    init: initMapControls,
    focusResource,
    blurResource,
    setMapReady
  } = svgMapInteractions;

  const { onMapLoaded } = useOnMapLoaded((svg) => {
    svgRef.current = svg;
    initMapControls();
  });

  React.useEffect(() => {
    const onFocusResource = (resource: IResource | null) => {
      if (!resource) {
        //blurResource();
        return;
      }
      setMapReady();
      focusResource(resource?.id);
    };

    events.register('onFocusResource', onFocusResource);

    return () => {
      events.unregister('onFocusResource', onFocusResource);
    };
  }, [focusResource, blurResource]);

  if (!floor?.id) return null;

  return (
    <>
      <div
        className={'map-container-ref absolute inset-0 overflow-hidden'}
        ref={mapContainer}
      >
        <div className={'v2-map'}>
          <div className={'item-container'}>
            <div
              className={'item-wrapper'}
              ref={mapRef}
              key={floor.id}
              style={{
                transformOrigin: 'top left',
                perspectiveOrigin: 'center center',
                position: 'absolute',
                left: 0,
                boxSizing: 'border-box',
                padding: 0
              }}
            >
              <FloorMap floor={floor} onMapLoaded={onMapLoaded} />
              <MapInteractiveZones
                svgRef={svgRef}
                onReady={() => {
                  if (focussedResourceId) {
                    setTimeout(() => {
                      events.trigger('onFocusResource', {
                        id: focussedResourceId
                      } as IResource);
                    }, 100);
                  }
                }}
                interactionsDisabled
              />
            </div>
          </div>
        </div>
        {enableControls && (
          <MapViewSvgMiniMap
            mapInteractor={svgMapInteractions}
            width={100}
            height={100}
            disableOnFocus={false}
          />
        )}
      </div>
    </>
  );
};

export const MapViewEmbedded: React.FC<MapViewEmbeddedProps> = (props) => {
  const { officeId, floorId } = props;
  const {
    userContext: { authenticatedUser }
  } = useAuthenticated();

  const office = authenticatedUser?.offices?.offices?.find(
    (office) => office.id === officeId
  );

  const { floors: allFloors } = useFloors(officeId, null);

  const floor = allFloors?.find((f) => f.id === floorId);

  if (!office || !floor) return 'Loading...';

  return (
    <MapProvider
      officeId={office.id}
      floorId={floor.id}
      startDate={props.date}
      key={`${props.focussedResourceId}-${floor.id}`}
      isEmbedded
    >
      <MapViewEmbeddedInner {...props} />
    </MapProvider>
  );
};
